import React from 'react';
import { Link } from 'react-router-dom';
import { Play, Award, Camera, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import SEOSection from './SEOSection';

const Home = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <SEO 
        title="Vidéaste Mariage Alsace & Strasbourg | Film de Mariage Professionnel - Yastudio"
        description="Vidéaste mariage professionnel en Alsace, Strasbourg, Mulhouse, Colmar. Films de mariage cinématiques avec drone. Reportage vidéo mariage Bas-Rhin & Haut-Rhin. Devis gratuit."
        keywords="vidéaste mariage Alsace, vidéaste mariage Strasbourg, film de mariage Alsace, vidéo mariage Grand Est, vidéaste mariage Mulhouse, vidéaste mariage Colmar, vidéaste mariage Bas-Rhin, vidéaste mariage Haut-Rhin, film mariage drone Alsace, reportage vidéo mariage, vidéo mariage cinématique, teaser mariage"
      />
      {/* Hero Section with Video */}
      <section className="relative h-screen overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        >
          <source src="https://customer-assets.emergentagent.com/job_alsace-wedding-pro/artifacts/fuztto5z_clip.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black"></div>
        
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6 tracking-tight leading-tight">
            Vidéaste Mariage<br />
            <span className="text-light-pink">en Alsace</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl">
            Capturez les Moments Inoubliables de Votre Jour J
          </p>
          <div className="flex gap-4 flex-wrap justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-black hover:bg-gray-200 text-lg px-8 py-6 rounded-full">
                Demander un Devis
              </Button>
            </Link>
            <Link to="/tarifs">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-black text-lg px-8 py-6 rounded-full">
                Voir les Tarifs
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 px-4 bg-black">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Bienvenue sur <span className="text-light-pink">Yastudio</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              <strong className="text-white">Vidéaste mariage professionnel</strong> spécialisé dans la réalisation de <strong className="text-white">films de mariage en Alsace</strong>, 
              à <strong className="text-white">Strasbourg</strong>, <strong className="text-white">Mulhouse</strong>, <strong className="text-white">Colmar</strong> et dans toute la région <strong className="text-white">Grand Est</strong>. 
              Depuis plusieurs années, je mets mon expertise et ma passion pour la <strong className="text-white">vidéo mariage cinématique</strong> au service des couples souhaitant 
              immortaliser leur journée exceptionnelle avec un <strong className="text-white">film mariage de qualité professionnelle</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-zinc-950">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">
            Un Film de Mariage à la Hauteur<br />de Vos Souvenirs
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-zinc-900 p-8 rounded-lg hover:transform hover:scale-105 transition-all duration-300">
              <div className="w-16 h-16 bg-light-pink rounded-full flex items-center justify-center mb-6">
                <Camera className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-2xl font-semibold mb-4">Matériel Professionnel</h3>
              <p className="text-gray-400 leading-relaxed">
                Caméras 4K, stabilisateurs, drones et équipement de dernière génération 
                pour un rendu d'une qualité exceptionnelle.
              </p>
            </div>

            <div className="bg-zinc-900 p-8 rounded-lg hover:transform hover:scale-105 transition-all duration-300">
              <div className="w-16 h-16 bg-mid-blue rounded-full flex items-center justify-center mb-6">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-semibold mb-4">Vidéaste Expérimenté</h3>
              <p className="text-gray-400 leading-relaxed">
                Plusieurs années d'expérience dans la capture d'événements et dans la 
                réalisation de films de mariage en Alsace.
              </p>
            </div>

            <div className="bg-zinc-900 p-8 rounded-lg hover:transform hover:scale-105 transition-all duration-300">
              <div className="w-16 h-16 bg-mid-purple rounded-full flex items-center justify-center mb-6">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-semibold mb-4">Approche Personnalisée</h3>
              <p className="text-gray-400 leading-relaxed">
                Chaque couple est unique, chaque film est réalisé selon vos envies et 
                votre histoire avec sensibilité et créativité.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="py-20 px-4 bg-black">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_alsace-wedding-pro/artifacts/ub2hh801_DSC01034.jpeg"
                alt="Vidéaste mariage professionnel Alsace Strasbourg"
                className="rounded-lg shadow-2xl w-full h-auto"
              />
            </div>
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Vidéaste Mariage à Strasbourg<br />
                <span className="text-light-pink">& dans Tout le Grand Est</span>
              </h2>
              <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                La création de votre <strong className="text-white">film de mariage</strong> est un processus collaboratif. En tant que 
                <strong className="text-white"> vidéaste mariage à Strasbourg centre-ville</strong> et dans toute l'Alsace, je prends 
                le temps de comprendre vos attentes pour réaliser un <strong className="text-white">reportage vidéo mariage</strong> qui 
                vous ressemble.
              </p>
              <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                Je suis également <strong className="text-white">pilote de drone certifié</strong>, ce qui me permet 
                d'offrir des <strong className="text-white">films mariage avec drone</strong> pour des prises de vue aériennes époustouflantes. 
                Que vous recherchiez un <strong className="text-white">style vidéo mariage vintage</strong> ou une 
                <strong className="text-white"> vidéo mariage cinématique</strong> moderne, je m'adapte à vos envies.
              </p>
              <Link to="/about">
                <Button size="lg" className="bg-light-pink text-black hover:bg-light-pink/90 rounded-full">
                  En Savoir Plus
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-20 px-4 bg-zinc-950">
        <div className="max-w-4xl mx-auto text-center">
          <blockquote className="text-2xl md:text-3xl font-light italic text-gray-300 leading-relaxed">
            "Une journée de mariage passe en un éclair, mais ce sont les images qui figent l'éphémère — 
            quand tout s'efface, seules les photos et les vidéos restent pour raconter l'amour tel qu'il 
            a été vécu. <span className="text-light-pink font-semibold">Faites le bon choix avec Yastudio</span>, 
            pour garantir des souvenirs racontés de la plus belle manière."
          </blockquote>
        </div>
      </section>

      {/* Video Gallery Section */}
      <section className="py-20 px-4 bg-zinc-950">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-6">
            Nos Réalisations
          </h2>
          <p className="text-xl text-gray-300 text-center mb-16">
            Découvrez nos films de mariage en Alsace
          </p>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Video 1 - YouTube embed (première vidéo) */}
            <div className="bg-black rounded-lg overflow-hidden border border-zinc-800">
              <div className="relative" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src="https://www.youtube.com/embed/qn0dPoEEVUI"
                  title="Film de Mariage Yastudio"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">Film de Mariage Complet</h3>
                <p className="text-gray-400">Un exemple de nos réalisations en Alsace</p>
              </div>
            </div>

            {/* Video 2 - Our local video with hover autoplay */}
            <div className="bg-black rounded-lg overflow-hidden border border-zinc-800 group">
              <div className="relative" style={{ paddingBottom: '56.25%' }}>
                <video 
                  controls 
                  loop
                  muted
                  playsInline
                  className="absolute top-0 left-0 w-full h-full object-cover"
                  poster="https://customer-assets.emergentagent.com/job_alsace-wedding-pro/artifacts/ub2hh801_DSC01034.jpeg"
                  preload="metadata"
                  onMouseEnter={(e) => e.target.play()}
                  onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                >
                  <source src="https://customer-assets.emergentagent.com/job_alsace-wedding-pro/artifacts/fuztto5z_clip.mp4" type="video/mp4" />
                  Votre navigateur ne supporte pas la lecture de vidéos.
                </video>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">Film de Mariage - Teaser</h3>
                <p className="text-gray-400">Survolez pour prévisualiser notre style cinématique</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 bg-black">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">
            Témoignages Clients
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-zinc-900 p-8 rounded-lg border border-zinc-800">
              <p className="text-lg text-gray-300 mb-6 italic leading-relaxed">
                "Un souvenir incroyable grâce à Yastudio ! Notre film de mariage est une véritable 
                œuvre d'art. Les prises de vue avec le drone sont époustouflantes et l'émotion est 
                parfaitement capturée. Merci pour votre professionnalisme et votre discrétion !"
              </p>
              <p className="text-white font-semibold">— Claire & Julien, Strasbourg</p>
            </div>

            <div className="bg-zinc-900 p-8 rounded-lg border border-zinc-800">
              <p className="text-lg text-gray-300 mb-6 italic leading-relaxed">
                "Un vidéaste professionnel, créatif et à l'écoute. Le film de notre mariage est 
                magnifique, et nous n'aurions pas pu rêver mieux. Le service est impeccable et le 
                rendu est au-delà de nos attentes."
              </p>
              <p className="text-white font-semibold">— Sophie & Marc, Mulhouse</p>
            </div>
          </div>

          <div className="text-center mt-12">
            <a 
              href="https://www.mariages.net/video-mariage/yastudio-production--e188079#gallery" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-light-pink hover:text-light-pink/80 text-lg font-semibold transition-colors"
            >
              Voir tous nos avis sur Mariages.net →
            </a>
          </div>
        </div>
      </section>

      {/* SEO Section - Zones d'intervention */}
      <SEOSection />

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-zinc-950 to-black">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Prêt à Immortaliser Votre Mariage ?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Contactez-moi dès aujourd'hui pour obtenir un devis personnalisé
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-light-pink text-black hover:bg-light-pink/90 text-lg px-12 py-6 rounded-full">
              Demander un Devis Gratuit
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;